import React, { useContext, useState, useEffect } from "react";
import {
  Stack,
  Box,
  Grid2 as Grid,
  TextField,
  Typography,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
} from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import LinearProgress from "@mui/material/LinearProgress";
import { useMutation } from "@apollo/client";
import { useMutation as mutationDeleteImage } from "react-query";
//Icons
import { BiMinusCircle } from "react-icons/bi";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

//Srcs
import "../../Style/dialogStyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { translateLauguage } from "../../Function/Translate";
import { CREATE_LEVEL, UPDATE_LEVEL } from "../../Schema/Level";
import { SelectLevel } from "../../Function/DynamicSelect";

export default function LevelForm({
  open,
  handleClose,
  setRefetch,
  editData,
  dialogTitle,
}) {
  // console.log("levelEditData", editData);
  // ===================== Change Language ==========================
  const { language, setAlert } = useContext(AuthContext);
  const { t } = translateLauguage(language);

  const [loading, setLoading] = useState(false);

  const [newLevelVal, setNewLevelVal] = useState({ id: "", title: "" });

  //======================== Create =========================
  const [createLevel] = useMutation(CREATE_LEVEL, {
    onCompleted: ({ createLevel }) => {
      // console.log("createLevel::", createLevel);
      setLoading(false);
      if (createLevel?.is_success === true) {
        setAlert(true, "success", createLevel?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createLevel?.message);
      }
    },
    onError: (error) => {
      setLoading(false);
      setAlert(true, "error", error?.message);
    },
  });
  //======================== Update =========================
  const [updateLevel] = useMutation(UPDATE_LEVEL, {
    onCompleted: ({ updateLevel }) => {
      // console.log("updateLevel::", updateLevel);
      setLoading(false);
      if (updateLevel?.is_success === true) {
        setAlert(true, "success", updateLevel?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateLevel?.message);
      }
    },
    onError: (error) => {
      setLoading(false);
      setAlert(true, "error", error?.message);
    },
  });

  const [checkNextLevel, setCheckNextLevel] = useState("Next_Level");

  // console.log("checkNextLevel", checkNextLevel);

  //fromik
  const CheckValidation = Yup.object().shape({
    level_name: Yup.string().required(t("require")),
    remark: Yup.string(),
  });

  const CheckValidationUpdate = Yup.object().shape({
    level_name: Yup.string().required(t("require")),
    next_level_id:
      checkNextLevel === "Certificate_Offer"
        ? Yup.string()
        : Yup.string().required(t("require")),
    remark: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      level_name: "",
      remark: "",
      next_level_id: "",
    },

    validationSchema:
      dialogTitle === "Create" ? CheckValidation : CheckValidationUpdate,
    onSubmit: async (submitValues) => {

      setLoading(true);
      if (dialogTitle === "Create") {
        createLevel({
          variables: {
            input: {
              level_name: submitValues?.level_name?.trim(),
              remark: submitValues?.remark,
            },
          },
        });
      } else {
        updateLevel({
          variables: {
            id: editData?._id,
            input: {
              level_name: submitValues?.level_name?.trim(),
              remark: submitValues?.remark,
              after_finish: checkNextLevel,
              next_level_id:
                checkNextLevel === "Certificate_Offer"
                  ? null
                  : values?.next_level_id,
            },
          },
        });
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    resetForm,
    setFieldValue,
  } = formik;

  useEffect(() => {
    if (dialogTitle === "Create") {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    setFieldValue("next_level_id", newLevelVal?.id);
  }, [newLevelVal?.id]);

  useEffect(() => {
    if (editData && dialogTitle === "Update") {
      setFieldValue("level_name", editData?.level_name);
      setFieldValue("remark", editData?.remark);
      setCheckNextLevel(editData?.after_finish ?? null);
      setNewLevelVal({
        id: editData?.next_level_id?._id,
        title: editData?.next_level_id?.level_name,
      });
    }
  }, [editData]);

  return (
    <Dialog open={open} className="dialog-container" fullWidth maxWidth="sm">
      <DialogTitle sx={{ padding: "10px 15px 10px 20px " }}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" justifyContent="center">
            <Typography
              className={language === "en" ? "dialog-title" : "dialog-title-kh"}
            >
              {dialogTitle === "Create"
                ? t("dialog_title_create_grade_level")
                : t("dialog_title_update_grade_level")}
            </Typography>
          </Stack>
          <Stack direction="column" justifyContent="center">
            <IconButton onClick={handleClose}>
              <BiMinusCircle className="close-icon" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("grade_level")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("grade_level")}
                  {...getFieldProps("level_name")}
                  error={Boolean(touched.level_name && errors.level_name)}
                  helperText={touched.level_name && errors.level_name}
                />
              </Grid>

              {dialogTitle === "Create" ? null : (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Stack direction="row" spacing={1}>
                      <Typography
                        className={
                          language === "en" ? "field-title" : "field-title-kh"
                        }
                      >
                        {t("after_finish")}
                      </Typography>
                      <Typography
                        className={
                          language === "en" ? "field-title" : "field-title-kh"
                        }
                        sx={{ color: "red !important" }}
                      >
                        {editData?.after_finish === null ? t("require") : null}
                      </Typography>
                    </Stack>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={checkNextLevel}
                        onChange={(e) => setCheckNextLevel(e.target.value)}
                      >
                        <MenuItem value="Next_Level">
                          {t("next_level")}
                        </MenuItem>
                        <MenuItem value="Certificate_Offer">
                          {t("certificate_offer")}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {checkNextLevel === "Certificate_Offer" ? null : (
                    <Grid size={{ xs: 12 }}>
                      <Stack direction="row" spacing={1}>
                        <Typography
                          className={
                            language === "en" ? "field-title" : "field-title-kh"
                          }
                        >
                          {t("next_level")}
                        </Typography>
                        <Typography
                          className={
                            language === "en" ? "field-title" : "field-title-kh"
                          }
                          sx={{ color: "red !important" }}
                        >
                          {editData?.next_level_id === null
                            ? t("require")
                            : null}
                        </Typography>
                      </Stack>

                      <SelectLevel
                        selectedVal={newLevelVal}
                        setSelectedVal={setNewLevelVal}
                      />
                    </Grid>
                  )}
                </>
              )}
              {!!errors.next_level_id && touched.next_level_id && (
                <FormHelperText error id="outlined-adornment-password">
                  {errors.next_level_id}
                </FormHelperText>
              )}

              <Grid size={{ xs: 12 }}>
                <Typography
                  className={
                    language === "en" ? "field-title" : "field-title-kh"
                  }
                >
                  {t("remark")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  placeholder={t("remark")}
                  {...getFieldProps("remark")}
                />
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
      <DialogActions>
        <Grid size={{ xs: 12 }} className="create-container">
          {loading ? (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
            >
              {t("loading")}
            </Button>
          ) : (
            <Button
              className={language === "kh" ? "btn-create-kh" : "btn-create"}
              fullWidth
              onClick={handleSubmit}
            >
              {dialogTitle === "Create" ? t("btn-create") : t("btn-update")}
            </Button>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
}
